import { HttpMethod, IRequestModel} from "../models/IRequestModel";
import { IResponseModel } from "../models/IResponseModel";
import { Injectable } from "@angular/core";
import { ApiResponse } from '../models/ApiResponse';
import { ApiResponseError } from "../models/ApiResponseError";
import { disableDebugTools } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { CookieService } from "./CookieService";

export { HttpMethod, IRequestModel } from "../models/IRequestModel";
export { IResponseModel } from "../models/IResponseModel";
export { ValidationResponse } from "../models/validation/ValidationResponse";
export { ApiResponse } from "../models/ApiResponse";
export { ApiResponseError } from "../models/ApiResponseError";

 /**
  * Callback interface for responses with a JSON body parsed into the given type.
  * @type       type    Response object to parse from JSON.
  * @returns            True if the response has been handled.
  */
export interface ResponseHandler<T extends IResponseModel> {
    (response: ApiResponse<T>): boolean;
}

@Injectable({providedIn: 'root'})
export class ApiService {

    // For requests involving empty or json inputs and outputs only (no forms or plaintext)

    readonly baseDomain: string = "https://disomate.azurewebsites.net";
    authToken: string = "";

    // Note: Generics don't have to be provided, in this case T and U are empty types so don't read/write body content.

     /**
      * Sends an API request.
      * @type                   Response object to parse from JSON.
      * @param model            Request model containing content for the request.
      * @param respHandler      (Optional) Callback to handle response.
      */
    public SendRequest<T extends IResponseModel>(model: IRequestModel, respHandler?: ResponseHandler<T>): void {
        let modelClone: IRequestModel = { ...model }; // shallow clone
        delete modelClone.method;
        delete modelClone.uri;
        let jsonContent: string = JSON.stringify(modelClone);
        let isContentEmpty: boolean = true;
        for(let prop in modelClone) {
            if (modelClone.hasOwnProperty(prop)) {
                isContentEmpty = false;
            }
        }

        if (model.method != HttpMethod.GET && !isContentEmpty) {
            this.requestWithBody(model.method, this.GetFullPath(model.uri), jsonContent)
                .then((response) => {
                    let resp: ApiResponse<T> = new ApiResponse();
                    resp.statusCode = response.status;
                    resp.isSuccess = this.checkSuccessStatusCode(resp.statusCode);
                    response.json().then(function (json) {
                        if (resp.isSuccess) {
                            resp.values = json as unknown as T;;
                        } else {
                            resp.errors = json as unknown as ApiResponseError[];
                        }

                        if (respHandler(resp)) {
                            return true;
                        }
                        
                        this.BaseHandler(resp.statusCode);
                    });
                });
        } else {
            let uri: string = this.GetFullPath(model.uri);
            if (model.method == HttpMethod.GET && !isContentEmpty) {
                // Add model values as Uri query parameters to GET requests
                uri = uri + "?" + this.encodeQueryData(modelClone);
            }

            this.requestWithoutBody(model.method, uri)
                .then((response) => {
                    let resp: ApiResponse<T> = new ApiResponse();
                    resp.statusCode = response.status;
                    resp.isSuccess = this.checkSuccessStatusCode(resp.statusCode);
                    response.json().then(function (json) {
                        if (resp.isSuccess) {
                            resp.values = json as unknown as T;;
                        } else {
                            resp.errors = json as unknown as ApiResponseError[];
                        }

                        if (respHandler(resp)) {
                            return true;
                        }
                        
                        this.BaseHandler(resp.statusCode);
                    });
                });
        }
    }

    /**
     * Handler which executes on every response before request specific callbacks.
     * @param  {number}  status Response Status Code
     * @return {boolean}        True if handled by this handler
     */
    public BaseHandler(status: number): boolean {
        switch (status) {
            // TODO
            /* Add default responses here, for example;
            case "502":
                // 502 error
                dialogs.alert("An unexpected problem ocurred.");
                return true;
                break;
            */               
        }
        return false;
    }

    public GetFullPath(path: string): string {
        if (path[0] == '/') {
            return this.baseDomain + path;
        }
        return this.baseDomain + '/' + path;
    }

    public async requestWithBody(method: HttpMethod, fullUri: string, bodyContent: string) {
        let headers = {
            'Content-Type': 'application/json'
        };
        if (this.authToken != "") {
            headers['Authorization'] = "Bearer " + this.authToken;
        }

        const response = await fetch(fullUri, {
            method: method,
            mode: 'cors',
            cache: 'no-cache',
            headers: headers,
            redirect: 'follow',
            referrerPolicy: 'no-referrer',
            body: bodyContent
        });
        return response;
    }

    public async requestWithoutBody(method: HttpMethod, fullUri: string) {
        let headers = {
            'Content-Type': 'application/json'
        };
        if (this.authToken != "") {
            headers['Authorization'] = "Bearer " + this.authToken;
        }

        const response = await fetch(fullUri, {
            method: method,
            mode: 'cors',
            cache: 'no-cache',
            headers: headers,
            redirect: 'follow',
            referrerPolicy: 'no-referrer'
        });
        return response;
    }

    checkSuccessStatusCode(status: number): boolean {
        return status >= 200 && status <= 299;
    }

    encodeQueryData(data: any) {
        const ret = [];
        for (let d in data)
          ret.push(encodeURIComponent(d) + '=' + encodeURIComponent(data[d]));
        return ret.join('&');
     }
}


// Export models here to make importing easier

export { DiscordServerBasicInfo } from "../models/shared/DiscordServerBasicInfo";

export { ChangePasswordRequest } from "../models/request/auth/ChangePasswordRequest";
export { EmailVerificationRequest } from "../models/request/auth/EmailVerificationRequest";
export { EmailVerificationResendRequest } from "../models/request/auth/EmailVerificationResendRequest";
export { ForgottenPasswordCompleteRequest } from "../models/request/auth/ForgottenPasswordCompleteRequest";
export { ForgottenPasswordRequest } from "../models/request/auth/ForgottenPasswordRequest";
export { LoginRequest } from "../models/request/auth/LoginRequest";
export { RegisterRequest } from "../models/request/auth/RegisterRequest";
export { AddServerRequest } from "../models/request/servers/AddServerRequest";
export { DeleteServerRequest } from "../models/request/servers/DeleteServerRequest";
export { DiscordAuthRequest } from "../models/request/servers/DiscordAuthRequest";
export { ServerListRequest } from "../models/request/servers/ServerListRequest";

export { ChangePasswordResponse } from "../models/response/auth/ChangePasswordResponse";
export { EmailVerificationResendResponse } from "../models/response/auth/EmailVerificationResendResponse";
export { EmailVerificationResponse } from "../models/response/auth/EmailVerificationResponse";
export { ForgottenPasswordCompleteResponse } from "../models/response/auth/ForgottenPasswordCompleteResponse";
export { ForgottenPasswordResponse } from "../models/response/auth/ForgottenPasswordResponse";
export { LoginResponse } from "../models/response/auth/LoginResponse";
export { RegisterResponse } from "../models/response/auth/RegisterResponse";
export { AddServerResponse } from "../models/response/servers/AddServerResponse";
export { DeleteServerResponse } from "../models/response/servers/DeleteServerResponse";
export { DiscordAuthResponse} from "../models/response/servers/DiscordAuthResponse";
export { ServerListResponse } from "../models/response/servers/ServerListResponse";

